import React from 'react';
import type { SyncedKr } from '../../services/okrTrackerSyncService';

/**
 * OkrPickerCard — "select your OKRs" checklist on the employee magic-link
 * form. Presentational only: the page owns the sync fetch and the
 * add/remove writes; this card renders the synced KR list and the picked
 * state.
 *
 * The OKR Tracker (okr.rascalrepublic.com) stays the source of truth —
 * the banner link is always visible, and the empty state points there
 * instead of letting the employee type free-text OKRs.
 */

export const OKR_TRACKER_URL = 'https://okr.rascalrepublic.com';

export interface OkrPickerCardProps {
  krs: SyncedKr[];
  /** itemIds already on the submission (krId === itemId for picked KRs). */
  pickedIds: Set<string>;
  loading: boolean;
  error?: string | null;
  disabled?: boolean;
  onPick: (kr: SyncedKr) => void;
  onUnpick: (krId: string) => void;
  onRetry: () => void;
}

const OkrPickerCard: React.FC<OkrPickerCardProps> = ({
  krs, pickedIds, loading, error, disabled = false, onPick, onUnpick, onRetry,
}) => {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-5">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="text-base font-semibold text-stone-900">
          Select the OKRs for this review
        </div>
        {loading && (
          <span
            className="material-symbols-rounded animate-spin text-stone-400"
            style={{ fontSize: 20 }}
            aria-label="Syncing"
          >
            progress_activity
          </span>
        )}
      </div>
      <p className="text-sm text-stone-500 leading-snug mb-4">
        Synced from the OKR Tracker — the source of truth is{' '}
        <a
          href={OKR_TRACKER_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-700 underline underline-offset-2"
        >
          okr.rascalrepublic.com
        </a>
        . Tick the key results that apply to this review period.
      </p>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800 mb-3">
          Could not sync your OKRs: {error}
          <button
            type="button"
            onClick={onRetry}
            className="ml-2 font-medium underline underline-offset-2"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && krs.length === 0 && (
        <div className="rounded-lg bg-stone-50 border border-stone-200 px-4 py-3 text-sm text-stone-700">
          <div className="font-medium text-stone-900 mb-0.5">
            No OKRs mapped to you for this period
          </div>
          You can continue with the rest of the form. If you believe this is
          wrong, check your OKRs at{' '}
          <a
            href={OKR_TRACKER_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-700 underline underline-offset-2"
          >
            okr.rascalrepublic.com
          </a>{' '}
          or contact HR.
        </div>
      )}

      {krs.length > 0 && (
        <ul className="space-y-2">
          {krs.map(kr => {
            const picked = pickedIds.has(kr.krId);
            return (
              <li key={kr.krId}>
                <label
                  className={[
                    'flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors',
                    picked ? 'border-emerald-300 bg-emerald-50/50' : 'border-stone-200 hover:bg-stone-50',
                    disabled ? 'opacity-60 cursor-not-allowed' : '',
                  ].join(' ')}
                >
                  <input
                    type="checkbox"
                    checked={picked}
                    disabled={disabled}
                    onChange={() => (picked ? onUnpick(kr.krId) : onPick(kr))}
                    className="mt-0.5 h-4 w-4 accent-emerald-700"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-stone-900">
                      {kr.krNumber ? `${kr.krNumber} — ${kr.title}` : kr.title}
                    </span>
                    <span className="block text-xs text-stone-500 truncate">
                      {kr.objectiveTitle}
                      {kr.quarters.length > 0 && ` · ${kr.quarters.join(', ')}`}
                      {kr.bucket === 'assigned' && ' · cascaded to you'}
                    </span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default OkrPickerCard;
